import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageHeaderFilters, PageContent } from '@/components/ui/page'
import { Input } from '@/components/ui/input'
import { Users, Search } from 'lucide-react'
import { useState, useMemo } from 'react'
import { ContactsTable, type ContactsTableRow, type SortField, type SortDirection } from '@/components/ContactsTable'
import { useContacts, useContactSearch } from '@/hooks/useContacts'
import { useDebounce } from '@/hooks/useDebounce'
import { useContactMutations } from '@/hooks/useContactMutations'
import type { ContactResponseDTO } from '@/api/generated/models'
import { formatRelativeDateTime, formatUTCToLocalDateTime } from '@/lib/dateUtils'
import { ContactEditDialog } from '@/components/contacts/ContactEditDialog'
import { ContactGdprDeleteDialog } from '@/components/contacts/ContactGdprDeleteDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2 } from 'lucide-react'

export const Route = createFileRoute('/contacts')({
  component: Contacts,
  errorComponent: RouteErrorFallback,
})

// Helper function to format contact name
function formatContactName(firstName: string | null, lastName: string | null): string {
  const parts = []
  if (firstName) parts.push(firstName)
  if (lastName) parts.push(lastName)
  return parts.length > 0 ? parts.join(' ') : 'Unbekannt'
}

function Contacts() {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(25)
  const [selectedContact, setSelectedContact] = useState<ContactResponseDTO | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false)
  const [isNormalDeleteDialogOpen, setIsNormalDeleteDialogOpen] = useState<boolean>(false)
  const [isGdprDeleteDialogOpen, setIsGdprDeleteDialogOpen] = useState<boolean>(false)
  const [contactToDelete, setContactToDelete] = useState<ContactResponseDTO | null>(null)

  const { deleteContact } = useContactMutations()

  // Debounce search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 400)

  // Calculate offset for pagination
  const offset = (currentPage - 1) * pageSize

  // Fetch all contacts or search results based on search term
  const { data: allContacts, isLoading: isLoadingAll, error: errorAll } = useContacts(pageSize, offset)
  const { data: searchResults, isLoading: isLoadingSearch } = useContactSearch(
    debouncedSearchTerm,
    debouncedSearchTerm,
    debouncedSearchTerm,
    pageSize,
    offset
  )

  // Determine which data to use
  const contactsPage = debouncedSearchTerm ? searchResults : allContacts
  const contacts = contactsPage?.items
  const isLoading = debouncedSearchTerm ? isLoadingSearch : isLoadingAll
  const error = errorAll

  // Process contact data for ContactsTable component
  const contactsTableRows = useMemo((): ContactsTableRow[] => {
    if (!contacts) return []

    return contacts.map((contact: ContactResponseDTO) => {
      return {
        id: contact.id,
        firstName: contact.firstname || '',
        lastName: contact.lastname || '',
        fullName: formatContactName(contact.firstname, contact.lastname),
        phoneNumber: contact.phone_number,
        createdAt: formatRelativeDateTime(contact.created_at),
        createdAtRaw: contact.created_at,
        createdAtExact: formatUTCToLocalDateTime(contact.created_at),
      }
    })
  }, [contacts])

  // Apply client-side sorting
  const sortedRows = useMemo(() => {
    const sorted = [...contactsTableRows]

    sorted.sort((a, b) => {
      let compareA: string | number
      let compareB: string | number

      switch (sortField) {
        case 'name':
          compareA = a.fullName.toLowerCase()
          compareB = b.fullName.toLowerCase()
          break
        case 'phone':
          compareA = a.phoneNumber
          compareB = b.phoneNumber
          break
        case 'createdAt':
          compareA = new Date(a.createdAtRaw).getTime()
          compareB = new Date(b.createdAtRaw).getTime()
          break
        default:
          return 0
      }

      if (compareA < compareB) {
        return sortDirection === 'asc' ? -1 : 1
      }
      if (compareA > compareB) {
        return sortDirection === 'asc' ? 1 : -1
      }
      return 0
    })

    return sorted
  }, [contactsTableRows, sortField, sortDirection])

  // Handle sort field click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field with default ascending direction
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Handle row click to open edit dialog
  const handleRowClick = (row: ContactsTableRow) => {
    // Find the full contact data from the contacts array
    const contact = contacts?.find((c: ContactResponseDTO) => c.id === row.id)
    if (contact) {
      setSelectedContact(contact)
      setIsEditDialogOpen(true)
    }
  }

  // Handle normal delete click
  const handleDeleteClick = (row: ContactsTableRow) => {
    const contact = contacts?.find((c: ContactResponseDTO) => c.id === row.id)
    if (contact) {
      setContactToDelete(contact)
      setIsNormalDeleteDialogOpen(true)
    }
  }

  // Handle GDPR delete click
  const handleGdprDeleteClick = (row: ContactsTableRow) => {
    const contact = contacts?.find((c: ContactResponseDTO) => c.id === row.id)
    if (contact) {
      setContactToDelete(contact)
      setIsGdprDeleteDialogOpen(true)
    }
  }

  // Handle normal delete confirmation
  const handleNormalDeleteConfirm = async () => {
    if (!contactToDelete) return

    deleteContact.mutate(contactToDelete.id, {
      onSuccess: () => {
        setIsNormalDeleteDialogOpen(false)
        setContactToDelete(null)
      }
    })
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  // Reset to page 1 when search term changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const totalItems = contactsPage?.total_count ?? 0

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle hiddenOnMobile>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <PageHeaderHeading>Kontakte</PageHeaderHeading>
              </div>
              <PageHeaderDescription>Verwalten Sie Ihre Kundenkontakte</PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderFilters>
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Suche nach Name oder Telefonnummer..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </PageHeaderFilters>
          </PageHeaderContent>
        </PageHeader>

        <PageContent className="p-6">
          <ContactsTable
            rows={sortedRows}
            isLoading={isLoading}
            error={!!error}
            onRowClick={handleRowClick}
            onDeleteClick={handleDeleteClick}
            onGdprDeleteClick={handleGdprDeleteClick}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </PageContent>
      </PageCard>

      <ContactEditDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        contact={selectedContact}
      />

      {/* Normal Delete Confirmation Dialog */}
      <AlertDialog open={isNormalDeleteDialogOpen} onOpenChange={setIsNormalDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kontakt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie den Kontakt "{contactToDelete ? formatContactName(contactToDelete.firstname, contactToDelete.lastname) : ''}" löschen möchten?
              Der Kontakt wird entfernt, aber alle zugehörigen Anrufe, Tickets und Termine bleiben im System erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteContact.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleNormalDeleteConfirm}
              disabled={deleteContact.isPending}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteContact.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* GDPR Delete Dialog */}
      <ContactGdprDeleteDialog
        contact={contactToDelete}
        open={isGdprDeleteDialogOpen}
        onOpenChange={setIsGdprDeleteDialogOpen}
      />
    </PageContainer>
  )
}
